import { UserOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { EditIcon } from 'assets/images/svg';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { peopleActions } from 'reduxStore/people/slice';
import { PersonProfileImage } from 'zComponents/atoms';
import { Avatar, Button, Col, Flex, Form, Input, Row, Tooltip, Typography } from 'zui/Antd';

const { Text } = Typography;

const EditDrawerHeader = React.memo(({ personID, displayName, email, photoUrl, fBPersonID }) => {
	// ✅ Hooks
	const {
		token: {
			paddingXS,
			paddingLG,
			colorTextLabel,
			fontSizeHeading3,
			fontSizeHeading5,
			colorText,
			colorTextDescription,
			fontWeightStrong,
			fontSizeSM
		}
	} = theme.useToken();
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const imageColRef = useRef(null);
	const formColRef = useRef(null);
	const [form] = Form.useForm();

	// ✅ Local State
	const [isEdit, setIsEdit] = useState(false);
	const [errors, setErrors] = useState({ displayName: '', email: '' });

	// ✅ Permissions
	const canUpdatePerson = useManagePermittedRoles('updatePerson');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const isZezamiiUser = !!fBPersonID;

	const validationRules = useMemo(
		() => ({
			name: fieldsValidator.Name,
			email: isZezamiiUser ? [] : fieldsValidator.Email.filter((rule) => !rule.required)
		}),
		[isZezamiiUser]
	);

	useEffect(() => {
		form.setFieldsValue({ displayName, email });
		setErrors({ displayName: '', email: '' });
		setIsEdit(false);
	}, [form, displayName, email, personID]);

	useEffect(() => {
		if (isEdit && inputRef.current) inputRef.current.focus();
	}, [isEdit]);

	const handleFinish = useCallback(
		async (values) => {
			const trimmedName = values.displayName?.trim();
			const trimmedEmail = values.email?.trim();
			const newErrors = {
				displayName: trimmedName ? '' : 'Display name is required',
				email: !isZezamiiUser && !trimmedEmail ? 'Email is required' : ''
			};

			setErrors(newErrors);
			if (newErrors.displayName || newErrors.email) return;

			if (trimmedName !== displayName.trim()) {
				dispatch(peopleActions.updatePerson({ personID, data: { displayName: trimmedName } }));
			}
			if (!isZezamiiUser && trimmedEmail !== (email?.trim() || '')) {
				dispatch(peopleActions.updatePerson({ personID, data: { email: trimmedEmail } }));
			}
			setIsEdit(false);
		},
		[dispatch, personID, displayName, email, isZezamiiUser]
	);

	const handleCancel = useCallback(() => {
		form.setFieldsValue({ displayName, email });
		setErrors({ displayName: '', email: '' });
		setIsEdit(false);
	}, [form, displayName, email]);

	const toggleEditMode = useCallback(
		(e) => {
			e.stopPropagation();
			if (!canUpdatePerson) return;
			isEdit ? handleCancel() : setIsEdit(!isEdit);
		},
		[isEdit, handleCancel, canUpdatePerson]
	);

	useEffect(() => {
		const handleClickOutside = (event) => {
			const outsideForm = formColRef.current && !formColRef.current.contains(event.target);
			const outsideImage = imageColRef.current && !imageColRef.current.contains(event.target);
			const insideModal =
				event.target.closest('.ant-modal') ||
				event.target.closest('.antd-img-crop') ||
				event.target.closest('.rc-image-preview');

			if (isEdit && outsideForm && outsideImage && !insideModal) {
				form.validateFields()
					.then(handleFinish)
					.catch(() => {});
			}
		};

		if (isEdit) document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isEdit, form, handleFinish]);

	const baseInputStyle = useMemo(
		() => ({
			fontWeight: fontWeightStrong,
			padding: 0
		}),
		[fontWeightStrong]
	);

	const styles = {
		displayName: { ...baseInputStyle, fontSize: fontSizeHeading3, lineHeight: '32px' },
		email: {
			...baseInputStyle,
			fontSize: fontSizeHeading5,
			lineHeight: '24px',
			cursor: isZezamiiUser ? 'not-allowed' : 'text'
		}
	};

	return (
		<Row align='middle' gutter={[paddingLG, paddingXS]}>
			<Col ref={imageColRef}>
				{photoUrl ? (
					<PersonProfileImage
						size={54}
						photoUrl={photoUrl}
						name={displayName}
						entityID={personID}
						entityName={'People'}
						shape={'square'}
						edit={isEdit}
					/>
				) : (
					<Avatar
						size={54}
						icon={<UserOutlined style={{ fontSize: 41 }} />}
						style={{ backgroundColor: '#7D4CDB' }}
						shape='square'
					/>
				)}
			</Col>

			<Col flex='auto' ref={formColRef}>
				<Form form={form} layout='vertical' onFinish={handleFinish}>
					<Flex vertical gap={6}>
						<Form.Item name='displayName' style={{ marginBottom: 0 }} rules={validationRules.name}>
							<Input
								ref={inputRef}
								disabled={!isEdit}
								onPressEnter={() => form.submit()}
								style={styles.displayName}
								variant={errors.displayName ? 'filled' : 'borderless'}
								styles={{ input: { color: isEdit ? colorTextDescription : colorText } }}
								status={errors.displayName ? 'error' : 'default'}
							/>
						</Form.Item>
						{errors.displayName && (
							<Text type='danger' style={{ fontSize: fontSizeSM }}>
								{errors.displayName}
							</Text>
						)}
					</Flex>

					<Flex vertical gap={6}>
						<Flex align='center' gap={8}>
							<Tooltip title={isEdit && isZezamiiUser && "Email can't be edited for Zezamii users"}>
								<Form.Item name='email' style={{ margin: 0 }} rules={validationRules.email}>
									<Input
										placeholder={!isEdit || isZezamiiUser ? 'Add email' : 'Email'}
										disabled={!isEdit || isZezamiiUser}
										onPressEnter={() => form.submit()}
										style={styles.email}
										variant={errors.email ? 'filled' : 'borderless'}
										styles={{
											input: {
												color: isEdit && !isZezamiiUser ? colorTextDescription : colorTextLabel,
												cursor: isZezamiiUser ? 'not-allowed' : 'text'
											}
										}}
										status={errors.email ? 'error' : 'default'}
									/>
								</Form.Item>
							</Tooltip>
						</Flex>
						{errors.email && (
							<Text type='danger' style={{ fontSize: fontSizeSM }}>
								{errors.email}
							</Text>
						)}
					</Flex>
				</Form>
			</Col>

			<Col style={{ alignSelf: 'flex-start' }}>
				<Tooltip title={!canUpdatePerson ? permissionDeniedMessage : ''}>
					<Button
						onClick={toggleEditMode}
						type='text'
						size='small'
						disabled={!canUpdatePerson}
						icon={<EditIcon color='currentColor' />}
					/>
				</Tooltip>
			</Col>
		</Row>
	);
});

EditDrawerHeader.displayName = 'EditDrawerHeader';

export default EditDrawerHeader;
